import { useState, useEffect, useRef } from 'react'
import { createWorkspace } from '../api/vestige.js'

function Field({ label, id, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs text-text-dim tracking-wide">
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs text-red" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

const INPUT_CLASS =
  'w-full bg-surface border border-border rounded px-3 py-2 text-sm text-text font-mono placeholder-text-faint focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors'

export default function CreateWorkspaceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    agentId: '',
    basePath: '',
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const firstInputRef = useRef(null)

  useEffect(() => {
    firstInputRef.current?.focus()

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const validate = () => {
    const next = {}
    if (!form.name.trim()) next.name = 'Name is required'
    if (!form.basePath.trim()) next.basePath = 'Base path is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      // snake_case: CreateWorkspaceRequest renames agent_id and base_path via
      // @JsonProperty, so camelCase keys are dropped and the @NotBlank base_path
      // then fails validation with a 400.
      const workspace = await createWorkspace({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        agent_id: form.agentId.trim() || undefined,
        base_path: form.basePath.trim(),
      })
      onCreated(workspace)
      onClose()
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ground/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md bg-surface border border-border-bright rounded-xl shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
          <h2 id="modal-title" className="text-sm font-semibold text-text tracking-wide">
            New workspace
          </h2>
          <button
            onClick={onClose}
            className="text-text-dim hover:text-text transition-colors p-1 rounded focus-visible:outline-accent"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="px-6 py-5 flex flex-col gap-4">
            <Field label="Name" id="ws-name" error={errors.name}>
              <input
                ref={firstInputRef}
                id="ws-name"
                type="text"
                value={form.name}
                onChange={setField('name')}
                placeholder="production-agent-1"
                className={INPUT_CLASS}
                autoComplete="off"
                aria-required="true"
                aria-invalid={!!errors.name}
              />
            </Field>

            <Field label="Description" id="ws-desc">
              <input
                id="ws-desc"
                type="text"
                value={form.description}
                onChange={setField('description')}
                placeholder="What this workspace does"
                className={INPUT_CLASS}
                autoComplete="off"
              />
            </Field>

            <Field label="Agent ID" id="ws-agent">
              <input
                id="ws-agent"
                type="text"
                value={form.agentId}
                onChange={setField('agentId')}
                placeholder="agent-abc123"
                className={INPUT_CLASS}
                autoComplete="off"
              />
            </Field>

            <Field label="Base path" id="ws-path" error={errors.basePath}>
              <input
                id="ws-path"
                type="text"
                value={form.basePath}
                onChange={setField('basePath')}
                placeholder="/opt/agents/workspace-1"
                className={INPUT_CLASS}
                autoComplete="off"
                aria-required="true"
                aria-invalid={!!errors.basePath}
              />
            </Field>

            {submitError && (
              <p className="text-sm text-red bg-red-faint rounded px-3 py-2" role="alert">
                {submitError}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs text-text-dim border border-border rounded hover:border-border-bright hover:text-text transition-colors focus-visible:outline-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-xs font-medium bg-accent text-ground rounded hover:bg-accent-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-accent"
            >
              {submitting ? 'Creating…' : 'Create workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
